import {schema} from '@/common/spec/spec-cache';
import _ from '@/common/helpers';

function getPropertyLinks(property) {
    const refArray = (() => {
        if (property.allOf) {
            return property.allOf;
        }
        if (property.items) {
            if (property.items.allOf) {
                return property.items.allOf;
            }
            if (property.items.length) {
                return property.items;
            }
        }
        return [property];
    })();
    return refArray.map(({$ref}) => $ref).filter(Boolean);
}

function isComplexType(type) {
    return [undefined, 'array', 'object'].includes(type);
}

/**
 * Process a definition in the definitions API file given a name
 * If the definition is found callbacks are executed
 * @param {Object} instructions - Instructions to process the definition
 * @param {string} instructions.name - Name of the definition
 * @param {Function} instructions.onDefinition
 * - Method to be called once the definition is found.
 * @param {Function} instructions.onProperty
 * - Method to be called once a property or item.property is found.
 * @param {Function} instructions.onChildProperty
 * - Method to be called once a link to another definition is found.
 */
function processDefinition({
    name, onDefinition, onProperty, onChildProperty,
}) {
    const definition = schema[name];
    if (!definition) {
        return;
    }
    if (onDefinition) {
        onDefinition(name, definition);
    }
    if (onProperty) {
        const {properties, items} = definition;
        const processProperty = (propertyName, property) => {
            if (onProperty && !onProperty(propertyName, property)) {
                return;
            }
            const links = getPropertyLinks(property);
            links.forEach(
                (link) => {
                    const childPropertyName = link.replace('#/components/schemas/', '');
                    if (onChildProperty) {
                        onChildProperty(propertyName, childPropertyName);
                    }
                },
            );
        };
        const children = properties || (items && items.properties);
        if (children) {
            Object.keys(children).forEach(
                childName => processProperty(childName, children[childName]),
            );
        } else {
            processProperty(name, schema[name]);
        }
    }
}

const eventTypes = {};
const placeholders = {};
const entities = {};
const complexTypes = {};
const excludedFields = ['_links', 'customFields', 'httpHeaders'];

/**
 * Creates the instruction to process entity fields
 * @return {Object} instructions
 */
function entitiesFieldsProcessor(entity, entityDefinitionName) {
    return {
        name: entityDefinitionName,
        onDefinition(name, {type}) {
            entity.type = type;
            entity.fields = {};
        },
        onProperty(fieldName, property) {
            if (excludedFields.includes(fieldName)) {
                return false;
            }
            entity.fields[fieldName] = {
                name: fieldName,
                description: property.description,
                type: property.type,
            };
            return isComplexType(property.type);
        },
        onChildProperty(fieldName, complexTypeDefinitionName) {
            const complexTypeName = _.unCapitalize(complexTypeDefinitionName);
            if (!complexTypes[complexTypeName]) {
                const complexTypeEntity = {name: complexTypeName};
                processDefinition(
                    entitiesFieldsProcessor(complexTypeEntity, complexTypeDefinitionName),
                );
                complexTypes[complexTypeName] = complexTypeEntity;
            }
            const complex = complexTypes[complexTypeName];
            const fieldsKeys = Object.keys(complex.fields);
            if (fieldsKeys.length === 1) {
                // Type Alias
                entity.fields[fieldName].type = complex.fields[fieldsKeys[0]].type;
            } else {
                entity.fields[fieldName].type = entity.fields[fieldName].type || complex.type;
                entity.fields[fieldName].complex = complex;
                entity.fields[fieldName].isComplexType = true;
            }
        },
    };
}

/**
 * Creates the instruction to process placeholders and its children entities
 * @return {Object} instructions
 */
function placeholderProcessor(placeholderName, placeholderDefinitionName) {
    return {
        name: placeholderDefinitionName,
        onProperty(entityRoute) {
            const [entityName] = entityRoute.split('/').reverse();
            placeholders[placeholderName].entities[entityName] = {
                name: entityName,
                route: entityRoute.replace(/\//g, '.'),
                order: Object.keys(placeholders[placeholderName].entities).length,
                isDefault: placeholderName === entityName,
            };
            return true;
        },
        onChildProperty(entityRoute, entityDefinitionName) {
            const [entityName] = entityRoute.split('/').reverse();
            if (!entities[entityName]) {
                const entity = {};
                processDefinition(
                    entitiesFieldsProcessor(entity, entityDefinitionName),
                );
                entities[entityName] = entity;
            }
            placeholders[placeholderName].entities[entityName].fields = entities[entityName].fields;
        },
    };
}

/**
 * Creates the instruction to process eventTypes and its children placeholders
 * @return {Object} instructions
 */
function eventTypeProcessor() {
    return {
        name: 'Placeholders',
        onProperty(eventTypeName) {
            eventTypes[eventTypeName] = {
                name: eventTypeName,
                placeholders: [],
            };
            return true;
        },
        onChildProperty(eventTypeName, placeholderDefinitionName) {
            const placeholderName = _.unCapitalize(
                placeholderDefinitionName.replace('Placeholders', ''),
            );
            if (!placeholders[placeholderName]) {
                placeholders[placeholderName] = {
                    name: placeholderName,
                    entities: {},
                    order: Object.keys(placeholders).length,
                };
                processDefinition(placeholderProcessor(placeholderName, placeholderDefinitionName));
            }
            eventTypes[eventTypeName].placeholders.push(placeholders[placeholderName]);
        },
    };
}

processDefinition(eventTypeProcessor());

export {
    eventTypes,
    placeholders,
    entities,
    complexTypes,
};
